// -----------------------------------------------------------------------------
// Library
// -----------------------------------------------------------------------------
import React, { useEffect } from "react";
import { Route, Routes, Navigate, useLocation } from "react-router-dom";

// -----------------------------------------------------------------------------
// Components and pages
// -----------------------------------------------------------------------------
import ResetPassword from "components/ResetPassword/ResetPassword";
import EarningsPage from "pages/Dashboard/Earnings";
import { ClientMembership } from "pages/ClientPortal/ClientMembership";
import { AccountSettings } from "pages/ClientPortal/AccountSettings";
import ViewWorkout from "pages/ClientPortal/ViewWorkout";
import MyGym from "pages/ClientPortal/MyGym";
import OnDemand from "pages/ClientPortal/OnDemand";
import Live from "pages/ClientPortal/Live";
import Profile from "pages/ClientPortal/Profile";
import Home from "./Home";
import Workouts from "./Workouts";
import Members from "./Members";
import Analytics from "./Analytics";
import Settings from "./Settings";
import { PricingPlan } from "./PricingPlan";
import { PlanSelection } from "components/Payments/PlanSelection";
import ConfirmEmailChange from "components/ConfirmEmailChange";
import { LiveStreams } from "./LiveStream";
import { OfflineBanner } from "components/OfflineBanner";
import { Trainers } from "components/Trainers";
import Equipment from "./Equipment";

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------
const createCreatorRoute = (path, element, isUserCreator, defaultPath = "../") => {
	return <Route path={path} element={isUserCreator ? element : <Navigate to={defaultPath} />} />;
};

const createConsumerRoute = (path, element, isUserCreator, defaultPath = "../") => {
	return <Route path={path} element={isUserCreator ? <Navigate to={defaultPath} /> : element} />;
};

const SwitchRoute = ({ isUserCreator }) => {
	const { pathname } = useLocation();

	useEffect(() => {
		window.scrollTo(0, 0);
	}, [pathname]);
	return (
		<div className="sb-switch-route">
			<OfflineBanner isCreator={isUserCreator} />
			<Routes>
				{/* Default Home Route */}
				<Route exact path="/" element={isUserCreator ? <Home /> : <OnDemand isNormalUser />} />

				{/* Creator Portal */}
				{createCreatorRoute("workouts", <Workouts />, isUserCreator)}
				{createCreatorRoute("members", <Members />, isUserCreator)}
				{createCreatorRoute("earnings", <EarningsPage />, isUserCreator)}
				{createCreatorRoute("pricing-plan", <PricingPlan />, isUserCreator)}
				{createCreatorRoute("analytics", <Analytics />, isUserCreator)}
				{createCreatorRoute("settings", <Settings />, isUserCreator)}
				{createCreatorRoute("select-plan", <PlanSelection />, isUserCreator)}
				{createCreatorRoute("consumer", <OnDemand isNormalUser={!isUserCreator} />, isUserCreator)}
				{createCreatorRoute("consumer/on-demand", <OnDemand isNormalUser={!isUserCreator} />, isUserCreator)}
				{createCreatorRoute("consumer/live", <Live />, isUserCreator)}
				{createCreatorRoute("workouts/trainers", <Trainers />, isUserCreator)}
				{createCreatorRoute("workouts/equipment", <Equipment />, isUserCreator)}

				{/* Consumer Portal */}
				{createConsumerRoute("profile", <Profile />, isUserCreator)}
				{createConsumerRoute("account-settings", <AccountSettings />, isUserCreator)}
				{createConsumerRoute("consumer-membership", <ClientMembership />, isUserCreator)}
				{createConsumerRoute("my-gym", <MyGym />, isUserCreator)}

				{/* Shared Routes */}
				<Route path={`view-workout/:workoutID`} element={<ViewWorkout isUserCreator={isUserCreator} />} />
				<Route path={`view-live-workout/:workoutID`} element={<ViewWorkout isUserCreator={isUserCreator} />} />
				<Route path={`on-demand`} element={<OnDemand isNormalUser={!isUserCreator} />} />
				<Route path={`live`} element={<Live />} />
				<Route path={`reset-password/:token`} element={<ResetPassword />} />
				<Route path={`confirm-email-change/:userId/:token`} element={<ConfirmEmailChange />} />
				<Route path={`livestreams`} element={<LiveStreams />} />
				{/* 404 Route */}
				<Route path={`404`} element={<h1>404 Not Found</h1>} />

				<Route path="*" element={<Navigate to="/404" />} />
			</Routes>
		</div>
	);
};

export default SwitchRoute;
